import { Component, Input, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  SharedContact,
  SharedRecipient,
  SharedRecipientDetails,
} from '@compumark/order-management-client';
import { SharingService } from 'src/app/sharing/services/sharing.service';

import { GridOptionsService } from '../../../columns/column-definition/grid-options.service';
import { OrderSharingModalComponent } from '../../../sharing/components/order-sharing-modal/order-sharing-modal.component';

@Component({
  selector: 'app-bulk-share-order',
  templateUrl: './bulk-share-order.component.html',
  styleUrls: ['./bulk-share-order.component.scss'],
  standalone: false,
})
export class BulkShareOrderComponent {
  private dialog = inject(MatDialog);
  private gridOptionsService = inject(GridOptionsService);
  private sharingService = inject(SharingService);

  @Input() disabled = false;
  @Input() public tooltipPosition: 'above' | 'below' | 'before' | 'after' =
    'below';
  public tooltip = $localize`:@@BulkShareComponentBulkShareBtnTooltip:Share all results from selected order(s)`;

  get isSharingEnabled(): boolean {
    return this.sharingService.isSharingFeatureEnabledForCurrentMember();
  }

  openBulkShareModal(): void {
    if (this.disabled) {
      return;
    }
    const selectedNodes = this.gridOptionsService
      .getGridApi()
      ?.getSelectedNodes();

    if (!selectedNodes || selectedNodes.length === 0) {
      return;
    }

    const orderNumbers: string[] = [];
    const allSharedRecipientDetails: SharedRecipientDetails[] = [];

    selectedNodes.forEach((node) => {
      const data = node.data;
      if (data?.orderNumber) {
        orderNumbers.push(data.orderNumber);
      }
      const detailsList: SharedRecipientDetails[] =
        data?.sharedRecipientDetailsList;
      if (Array.isArray(detailsList)) {
        allSharedRecipientDetails.push(...detailsList);
      }
    });
    const mergedRecipientDetails = this.mergeSharedRecipientDetails(
      allSharedRecipientDetails,
    );

    this.dialog.open(OrderSharingModalComponent, {
      data: {
        isBulk: true,
        orderNumbers,
        selectedNodes,
        sharedRecipientDetails: mergedRecipientDetails,
      },
      width: '65vw',
      height: '90vh',
      disableClose: true,
    });
  }

  private mergeSharedRecipientDetails(
    detailsList: SharedRecipientDetails[],
  ): SharedRecipientDetails[] {
    if (!detailsList || detailsList.length === 0) {
      return [];
    }

    const contactMap = new Map<
      string,
      {
        contact: SharedContact;
        member: SharedRecipient;
        dateShared?: string;
        sharedBy?: any;
      }
    >();

    detailsList.forEach((details) => {
      (details.sharedTo ?? []).forEach((member: SharedRecipient) => {
        (member.contacts ?? []).forEach((contact: SharedContact) => {
          const key = `${member.memberId}-${contact.contactId}`;
          const existing = contactMap.get(key);

          if (!existing) {
            contactMap.set(key, {
              contact,
              member,
              dateShared: details.dateShared,
              sharedBy: details.sharedBy,
            });
          } else {
            const existingTime = this.parseDateTime(existing.dateShared);
            const currentTime = this.parseDateTime(details.dateShared);
            if (currentTime < existingTime) {
              contactMap.set(key, {
                contact,
                member,
                dateShared: details.dateShared,
                sharedBy: details.sharedBy,
              });
            }
          }
        });
      });
    });

    if (contactMap.size === 0) {
      return [];
    }
    const groupKey = (dateShared?: string, sharedByContactId?: number) =>
      `${sharedByContactId}-${dateShared}`;

    const groupMap = new Map<
      string,
      {
        sharedBy: any;
        dateShared?: string;
        memberMap: Map<number, SharedRecipient>;
      }
    >();

    contactMap.forEach(({ contact, member, dateShared, sharedBy }) => {
      const gKey = groupKey(dateShared, sharedBy?.contactId);
      if (!groupMap.has(gKey)) {
        groupMap.set(gKey, {
          sharedBy,
          dateShared,
          memberMap: new Map(),
        });
      }
      const group = groupMap.get(gKey)!;
      if (!group.memberMap.has(member.memberId!)) {
        group.memberMap.set(member.memberId!, {
          memberId: member.memberId,
          memberName: member.memberName,
          memberCity: member.memberCity,
          contacts: [],
        });
      }
      group.memberMap.get(member.memberId!)!.contacts!.push(contact);
    });

    return Array.from(groupMap.values()).map((group) => ({
      sharedBy: group.sharedBy,
      dateShared: group.dateShared,
      sharedTo: Array.from(group.memberMap.values()),
    }));
  }
  private parseDateTime(dateStr?: string): number {
    if (!dateStr) {
      return Infinity;
    }
    const [timePart, datePart] = dateStr.split(', ');
    if (!timePart || !datePart) {
      return Infinity;
    }
    const match = timePart.match(/(\d+):(\d+)(AM|PM)/);
    if (!match) {
      return Infinity;
    }
    let hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    const period = match[3];
    if (period === 'PM' && hours !== 12) {
      hours += 12;
    }
    if (period === 'AM' && hours === 12) {
      hours = 0;
    }
    const date = new Date(datePart);
    date.setHours(hours, minutes, 0, 0);
    return isNaN(date.getTime()) ? Infinity : date.getTime();
  }
}
